<template>
  <div class="flex flex-col gap-6 min-h-screen">
    <!-- Top Stats Cards -->
    <div class="grid grid-cols-4 gap-4">
      <!-- Exploited Equipment Card - 2/4 -->
      <div class="card col-span-2 bg-white">
        <div class="card-body">
          <h2 class="card-title text-lg mb-4">Eksplotuojami įrenginiai</h2>
          <div class="h-64 relative w-full">
            <Bar :data="equipmentChartData" :options="horizontalBarOptions" />
          </div>
        </div>
      </div>

      <!-- Total Pipeline Length Card - 1/4 -->
      <div class="card bg-red-500 text-white shadow-lg">
        <div class="card-body">
          <h2 class="text-xl font-semibold mb-2">
            Šilumotiekio vamzdynų bendras ilgis
          </h2>
          <p class="text-4xl font-bold">53524 m</p>
        </div>
      </div>

      <!-- Total Camera Length Card - 1/4 -->
      <div class="card bg-blue-500 text-white shadow-lg">
        <div class="card-body">
          <h2 class="text-xl font-semibold mb-2">
            Šilumotiekio apsauginių kanalų bendras ilgis
          </h2>
          <p class="text-4xl font-bold">10798 m</p>
        </div>
      </div>
    </div>

    <!-- Bar Chart Section -->
    <div class="card bg-white">
      <div class="card-body">
        <h2 class="card-title text-lg mb-6">
          Vamzdynų statistika pagal diametrą
        </h2>
        <div class="h-64 relative w-full">
          <Bar :data="diameterChartData" :options="barOptions" class="w-full" />
        </div>
      </div>
    </div>

    <div class="grid grid-cols-4 gap-4">
      <!-- Download Button -->
      <div class="col-span-1 card bg-white shadow-lg p-4">
        <div class="flex items-center justify-between">
          <span class="text-lg">Atsisiųsti objektų sąrašą</span>
          <button
            @click="handleDownload"
            :disabled="statisticsStore.isDownloading"
            class="btn btn-circle btn-outline text-red-500 border-red-500 hover:bg-red-500 hover:text-white transition-colors"
            :class="{
              'opacity-50 cursor-not-allowed': statisticsStore.isDownloading,
            }"
          >
            <svg
              v-if="!statisticsStore.isDownloading"
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
            <svg
              v-else
              class="animate-spin h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </button>
        </div>
        <div v-if="statisticsStore.error" class="mt-2 text-red-500 text-sm">
          {{ statisticsStore.error }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Bar } from "vue-chartjs";
import { useStatisticsStore } from "@/stores/statistics";
import { useAuthStore } from "@/stores/auth";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";

const statisticsStore = useStatisticsStore();
const authStore = useAuthStore();

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
);

const equipmentChartData = {
  labels: [
    "Šilumotiekio sklendės",
    "Šilumotiekio vamzdžių jungtys",
    "Šilumotiekio aklės",
    "Šilumotiekio kompensatoriai",
    "Šilumotiekio slėgio reguliatoriai",
    "Šilumotiekio šulinių dangčiai",
    "Šilumotiekio informacinis ženklas (sargelis)",
  ],
  datasets: [
    {
      data: [2, 140, 24, 16, 3, 358, 41],
      backgroundColor: "#3B82F6",
      barThickness: 8,
    },
  ],
};

const diameterChartData = {
  labels: [
    "<50",
    "50",
    "65",
    "80",
    "100",
    "125",
    "150",
    "175",
    "200",
    "250",
    "300",
    "350",
    "400",
    "nenurodyta",
  ],
  datasets: [
    {
      label: "Kiekis",
      data: [
        2818.6, 15010.0, 5685.8, 5370.2, 6557.9, 3437.2, 3363.9, 156.9, 1600.4,
        700.2, 4331.5, 2990.2, 54.3, 1446.7,
      ],
      backgroundColor: "#3B82F6",
    },
  ],
};

const horizontalBarOptions = {
  indexAxis: "y",
  maintainAspectRatio: false,
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      grid: {
        display: false,
      },
    },
    y: {
      grid: {
        display: false,
      },
    },
  },
};

const barOptions = {
  maintainAspectRatio: false,
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      max: 20000,
      ticks: {
        stepSize: 1000,
      },
    },
    x: {
      grid: {
        display: false,
      },
    },
  },
};

const handleDownload = async () => {
  try {
    await statisticsStore.downloadStatistics(
      `registras_org_${authStore.user.organization.id}.xlsx`,
    );
  } catch (error) {
    console.error("Download failed:", error);
  }
};
</script>

<style scoped>
.h-64 {
  height: 16rem;
  position: relative;
}
</style>
